import React, { useState, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const checkPasswordStrength = (password) => {
  let strength = 0;
  let feedback = [];
  if (password.length >= 8) strength += 25; else feedback.push('Mindestens 8 Zeichen');
  if (/[a-z]/.test(password)) strength += 25; else feedback.push('Kleinbuchstaben');
  if (/[A-Z]/.test(password)) strength += 25; else feedback.push('Großbuchstaben');
  if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) strength += 25; else feedback.push('Zahlen oder Sonderzeichen');
  let label = 'Sehr schwach';
  if (strength >= 100) label = 'Stark';
  else if (strength >= 75) label = 'Gut';
  else if (strength >= 50) label = 'Mittel';
  else if (strength >= 25) label = 'Schwach';
  return { strength, label, feedback };
};

const ForcePasswordChange = () => {
  const navigate = useNavigate();
  const { clearMustChangePassword, uebernehmeToken } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordStrength = useMemo(() => checkPasswordStrength(newPassword), [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }
    if (passwordStrength.strength < 50) {
      setError('Bitte ein stärkeres Passwort wählen');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Das neue Passwort darf nicht mit dem aktuellen Passwort übereinstimmen');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.changePassword(currentPassword, newPassword);
      // Neues Token VOR clearMustChangePassword/navigate übernehmen, sonst gilt noch das alte.
      if (res?.data?.access_token) uebernehmeToken(res.data.access_token);
      toast.success('Passwort erfolgreich geändert!');
      clearMustChangePassword();
      navigate('/');
    } catch (err) {
      const msg = fehlertext(err, 'Fehler beim Ändern des Passworts');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-0 shadow-xl" data-testid="force-password-change-card">
        <CardHeader className="space-y-1 pb-4 text-center">
          <div className="mx-auto h-14 w-14 bg-amber-100 rounded-full flex items-center justify-center mb-2">
            <Lock className="h-7 w-7 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold font-[Manrope]">
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Bitte ändern Sie Ihr Passwort bei der ersten Anmeldung.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <Input
                id="currentPassword"
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="h-11"
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {newPassword && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Progress value={passwordStrength.strength} className="h-2 flex-1" />
                    <span className={`text-xs font-medium ${
                      passwordStrength.strength >= 75 ? 'text-green-600' :
                      passwordStrength.strength >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Fehlt: {passwordStrength.feedback.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-11"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-blue-800 hover:bg-blue-900 font-medium"
              disabled={loading}
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                'Passwort ändern'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
